import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeEmail } from "@/lib/api";

export const GuideCapture = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeEmail(email, "guide_section");
      setDone(true);
      setEmail("");
      toast.success("Done. Your AI Workflow Guide is on its way.");
    } catch (err) {
      toast.error("Could not subscribe right now. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="guide"
      data-testid="guide-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#0A0A0A] text-white px-6 sm:px-12 lg:px-20 py-16 sm:py-20">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
              backgroundSize: "22px 22px",
            }}
          />
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#0052FF] blur-3xl opacity-30" />

          <div className="relative max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C8CFF] mb-5">
              Free Resource
            </div>
            <h2
              data-testid="guide-headline"
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
            >
              Get the Free AI Workflow Guide.
            </h2>
            <p
              data-testid="guide-subheadline"
              className="mt-5 text-lg text-white/70 max-w-2xl"
            >
              Discover practical AI workflows and tools that can save hours
              every week — sent straight to your inbox.
            </p>

            <form
              onSubmit={handleSubmit}
              data-testid="guide-email-form"
              className="mt-10 max-w-xl"
            >
              <div className="flex flex-col sm:flex-row gap-3 p-2 bg-white/5 border border-white/15 rounded-2xl backdrop-blur-sm focus-within:border-[#0052FF] focus-within:ring-2 focus-within:ring-[#0052FF]/40 transition-all">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  data-testid="guide-email-input"
                  aria-label="Email address"
                  className="flex-1 bg-transparent px-4 py-3 text-white placeholder:text-white/40 outline-none text-base"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="guide-submit-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-semibold py-3 px-5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-2 focus:ring-offset-[#0A0A0A]"
                >
                  {loading ? "Sending…" : "Send Me The Guide"}
                  {!loading && <ArrowRight size={16} />}
                </button>
              </div>
              {done && (
                <div
                  data-testid="guide-success"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white"
                >
                  <CheckCircle2 size={16} className="text-[#5C8CFF]" />
                  You&apos;re in. Watch your inbox.
                </div>
              )}
              <p className="mt-4 text-sm text-white/60">
                No spam. One practical AI workflow every week.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuideCapture;
